import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Dealer,
  FlexImageParameters,
  FlexImageV2RequestParams,
  RfqContactRole,
  RfqMode,
  RfqParams,
} from '../../../api/generated'
import { useClient } from '../../../utils/useClient'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getUrlCidValue } from '../../../utils/getUrlCidParameter'
import {
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../../store/appSlice'
import {
  getMarketDenomination,
  getRfqInput,
  getSessionInitDataState,
  getStartupData,
  getSummaryOrRfqOpenedFromPage,
  pushPageViewTrackingEvent,
  setRfqInput,
} from '../../../store/sessionDataSlice'
import { closeAllModals } from '../../../store/modalSlice'
import {
  TdsTextField,
  TdsTextarea,
  TdsButton,
  TdsCheckbox,
  TdsDropdown,
  TdsDropdownOption,
  TdsIcon,
  TdsMessage,
  TdsSpinner,
} from '@scania/tegel-react'
import {
  SvgChevronDown,
  SvgDocument,
  SvgEmail,
  SvgSend,
} from '../../../components/SvgImports'
import isEmail from '../../../utils/isEmail'
import { SESSION_FAILURE } from '../../../api/errors'
import { isTabletOrSmaller } from '../../../utils/screenQuery'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
} from '../../../store/truckInfoSlice'
import { FAMILY_ID_APPLICATION } from '../../../utils/constants'
import { MiniSummary } from '../OverviewPage/Minsummary'
import { GuidedViewPageType } from '../../../store/types'
import {
  buildRelativeAllViewPage,
  buildRelativeGvPage,
  buildRelativeOverviewPage,
} from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { ScaniaAdobeTrackingPageName } from '../../../utils/adobeAnalytics'
import { usePageUpdate } from '../../../utils/useBMPageUpdate'

const Overlay = styled.div`
  width: 100%;
  height: calc(100% - var(--header-height));
  max-width: 100vw;
  background-color: var(--tds-white);
  top: var(--header-height);
  position: fixed;
  padding-left: 40px;
  padding-right: 40px;
  display: flex;
  overflow: hidden;
  overflow-y: auto;
  overflow-x: hidden;
  ${CustomScrollbarMixin}

  form {
    display: flex;
    flex-direction: column;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-right: 16px;
    padding-left: 16px;
    flex-direction: column;
  }
`

const LeftSection = styled.div`
  width: 60%;
  padding-right: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    padding-right: 0px;
  }
`

const RightSection = styled.div`
  width: 40%;
  height: calc(100vh - var(--header-height));
  max-height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  top: 0px;
  right: 0px;
  position: sticky;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const SubHeader = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  margin-top: 16px;
`

const Header = styled.h1`
  display: flex;
  margin-top: 16px;
  margin-bottom: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 20px;
    margin-bottom: 8px;
  }
`

const MiniSummaryMobileWrapper = styled.div`
  display: none;
  flex-direction: column;
  border: 1px solid var(--tds-grey-400);
  border-radius: 4px;
  margin-bottom: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
  }
`

const MiniSummaryAccordionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`

const MiniSummaryMobileImageWrapper = styled.div`
  display: flex;
  height: 60px;
  width: 80px;
  align-items: center;
  justify-content: center;
  padding-left: 8px;

  img {
    height: 60px;
  }
`

const MiniSummaryMobileHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
`

const MiniSummaryMobileMoreInformationWrapper = styled.div`
  display: flex;
  margin-right: 8px;

  svg {
    height: 16px;
  }
`

const MiniSummaryMobileMoreInformationText = styled.div`
  font-size: 11px;
  margin-right: 4px;
  margin-bottom: 8px;
`

const MiniummaryMobileContent = styled.div``

const InputSection = styled.div`
  margin-bottom: 24px;
`

const StepperText = styled.h3`
  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const StepperWrapper = styled.div`
  margin-top: 32px;
  margin-bottom: 48px;
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-top: 16px;
    margin-bottom: 0px;
  }
`

const StepWrapper = styled.div`
  width: 33%;
`

const StepIconAndLineWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`

interface StepperIconWrapperProps {
  $currentStep: boolean
}

const StepperIconWrapper = styled.div<StepperIconWrapperProps>`
  border: ${(props) => (props.$currentStep ? '1px' : '0.5px')} solid
    ${(props) =>
      props.$currentStep ? 'var(--tds-information)' : 'var(--tds-grey-200)'};
  border-radius: 4px;
  background-color: var(--tds-grey-50);
  height: 40px;
  width: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 24px;
  }
`

const StepLine = styled.div`
  height: 1px;
  width: 160px;
  background-color: var(--tds-grey-300);
  margin-left: 30px;
  margin-right: 30px;
`

const StepText = styled.div`
  font-size: 14px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 12px;
    margin-right: 16px;
  }
`

const TruckSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-direction: column;
  height: calc(100% - 250px);
  justify-content: center;
  align-items: center;

  img {
    max-height: 100%;
    max-width: 100%;
    height: auto;
    width: auto;
    object-fit: contain;
  }
`

const MiniSummarySection = styled.div`
  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding: 8px;
  }
`

const SectionHeader = styled.h3`
  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 20px;
  }
`

const TextfieldsWrapper = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex-direction: column;
  }
`

const LabelCompulsory = styled.div`
  display: inline-block;
  color: red;
  margin-left: 2px;
  font-size: 12px;
  font-weight: bold;
`

const LabelInputField = styled.div`
  display: inline-block;
  font-size: 12px;
`

const TextFieldWrapper = styled.div`
  flex: 1 1 calc(50% - 20px);

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
  }
`

const TextAreaWrapper = styled.div`
  width: 45%;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
  }
`

const DontKnowDealerWrapper = styled.div`
  margin-top: 16px;
`

const DealerTextBox = styled.div`
  border: 1px solid var(--tds-grey-300);
  margin-bottom: 16px;
`

const DealerTextBoxHeader = styled.h5`
  margin-left: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
`

const DealerTextBoxAddress = styled.div`
  font-size: 11px;
  margin-left: 16px;
`

const DealerTextBoxPhone = styled.div`
  font-size: 11px;
  margin-top: 16px;
  margin-left: 16px;
  margin-bottom: 16px;
`

const MarketingConsentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  border: 1px solid var(--tds-grey-300);
  padding: 16px;
  font-weight: 12px;
`

const MarketingConsentText = styled.div`
  font-size: 12px;
  margin-bottom: 16px;
`

const CheckboxSmallText = styled.p`
  font-size: 12px;
  font-weight: normal;
  line-height: normal;
  color: var(--tds-checkbox-color);
`

const CheckboxWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  margin-left: 32px;
  gap: 8px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-left: 0px;
  }
`

const DemoModeMessage = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  color: red;
`

const ActionsWrapper = styled.div`
  margin-top: 40px;
  margin-bottom: 60px;
  display: flex;
  gap: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-top: 16px;
    margin-bottom: 16px;
    gap: 16px;
  }
`

const HeaderWrapperMiniSummary = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 6px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    margin-right: 0px;
  }
`

const HeaderMiniSummary = styled.h3`
  max-width: 60%;
  word-break: break-word;
`

const ApplicationWrapper = styled.div`
  background-color: var(--tds-grey-200);
  border-radius: 16px;
  padding: 6px 8px 6px 8px;
  height: fit-content;
`

interface GetConsultationProps {
  handleRfqSendClick: (rfq: RfqParams) => void
  handleFatalError: () => void
  handleSessionFailure: () => void
}

interface FormInput {
  firstName: string
  surname: string
  emailAddress: string
  telephoneNumber: string
  companyName: string
  postCode: string
  city: string
  organizationNumber: string
  vat: string
  role: RfqContactRole | null
  dontKnowDealer: boolean
  reseller: Dealer | null
  comment: string
  newsletter: boolean
  finance: boolean
  consent: boolean
}

enum TegelInputStatus {
  Default = 'default',
  Error = 'error',
  Success = 'success',
}

interface FormInputError {
  emailAddress?: TegelInputStatus
  companyName?: TegelInputStatus
  postCode?: TegelInputStatus
  reseller?: TegelInputStatus
  consent?: TegelInputStatus
  vat?: TegelInputStatus
}

interface FormInputErrorMessage {
  emailAddress: string
  companyName: string
  postCode: string
  reseller: string
  consent: string
  vat: string
}

export function GetConsultationPage({
  handleRfqSendClick,
  handleSessionFailure,
  handleFatalError,
}: GetConsultationProps): JSX.Element {
  const t2 = useTextsV2()
  const dispatch = useAppDispatch()
  const client = useClient()
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const previousRfqInput = useAppSelector(getRfqInput)
  const shouldShowDontKnowDealerCheckbox =
    marketSettings?.rfqRequiresDealerId === false &&
    marketSettings.sisEnabled === true
  const phoneNumberInstructions = t2(TextId.PHONE_NUMBER_INSTRUCTIONS)
  const startupData = useAppSelector(getStartupData)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionId = sessionInitData?.sessionId
  const marketDenomination = useAppSelector(getMarketDenomination)
  const truckInfo = useAppSelector(getTruckInfoState)
  const summaryOrRfqOpenedFromPage = useAppSelector(
    getSummaryOrRfqOpenedFromPage,
  )

  const [imageJpgUrl, setImageJpgUrl] = useState<string | undefined>(undefined)
  const [dealers, setDealers] = useState<Dealer[] | null>(null)
  const [formData, setFormData] = useState<FormInput>({
    firstName: previousRfqInput?.contactFirstName || '',
    surname: previousRfqInput?.contactFamilyName || '',
    emailAddress: previousRfqInput?.contactEmail || '',
    telephoneNumber: previousRfqInput?.contactPhone || '',
    companyName: previousRfqInput?.companyName || '',
    postCode: previousRfqInput?.companyPostalCode || '',
    city: previousRfqInput?.companyCity || '',
    organizationNumber: previousRfqInput?.organizationNumber || '',
    vat: previousRfqInput?.vatNumber || '',
    role: previousRfqInput?.contactRole || null,
    dontKnowDealer: false,
    reseller:
      dealers?.filter(
        (dealer) => dealer.scaniaId === previousRfqInput?.dealerId,
      )[0] || null,
    comment: previousRfqInput?.messageToDealer || '',
    newsletter: previousRfqInput?.newsletter || false,
    finance: previousRfqInput?.financeInterest || false,
    consent: previousRfqInput?.disclaimerAccepted || false,
  })
  const [formDataError, setFormDataError] = useState<FormInputError>({})
  const [formDataErrorMessage, setFormDataErrorMessage] =
    useState<FormInputErrorMessage>({
      emailAddress: '',
      companyName: '',
      postCode: '',
      reseller: '',
      consent: '',
      vat: '',
    })
  const [errorMessageSend, setErrorMessageSend] = useState<string>('')
  const [mobileShowMinisummary, setMobileShowMinisummary] =
    useState<boolean>(false)
  const miniSummaryState = useAppSelector(
    getLatestConfigChangeResult,
  )?.miniSummary
  const [application, setApplication] = useState<string>('')

  const handleInputChangeTextfield = (evt: any) => {
    if (!evt) {
      return
    }
    const changedField = evt.target.name
    const newValue = evt.target.value || ''
    setFormData((prevData) => {
      return {
        ...prevData,
        [changedField]: newValue,
      }
    })
    if (changedField === 'emailAddress' && newValue) {
      validateEmail(newValue)
    }
    if (changedField === 'companyName') {
      validateCompanyName(newValue)
    }
    if (changedField === 'postCode') {
      validatePostalCode(newValue)
    }
    if (changedField === 'vat') {
      validateVatNumber(newValue)
    }
  }

  const handleInputChangeCheckbox = (
    evt: CustomEvent<{
      checkboxId: string
      checked: boolean
      indeterminate: boolean
      value?: string
    }>,
  ) => {
    if (!evt) {
      return
    }

    const changedField = evt.detail.value
    if (!changedField) {
      return
    }
    const newValue = evt.detail.checked
    setFormData((prevData) => {
      return {
        ...prevData,
        [changedField]: newValue,
      }
    })
    if (changedField === 'consent') {
      validateCheckedConsent(newValue)
    }
    if (changedField === 'dontKnowDealer') {
      validateReseller(null, newValue)
      setFormData((prevData) => {
        return {
          ...prevData,
          reseller: null,
        }
      })
    }
  }

  const handleInputChangeDropdown = (
    evt: CustomEvent<{ name: string; value: string }>,
  ) => {
    if (!evt) {
      return
    }
    const changedField = evt.detail.name
    let newValue: Dealer | RfqContactRole | null = null
    if (changedField === 'reseller') {
      if (!dealers) {
        return
      }
      const selectedDealerId = evt.detail.value
      const selectedDealer = dealers.filter((dealer) => {
        return dealer.scaniaId === selectedDealerId
      })[0]
      newValue = selectedDealer
      validateReseller(selectedDealer, null)
      setFormData((prevData) => {
        return {
          ...prevData,
          dontKnowDealer: false,
        }
      })
    } else {
      newValue =
        evt.detail.value === RfqContactRole.Driver.toString()
          ? RfqContactRole.Driver
          : evt.detail.value === RfqContactRole.Owner.toString()
          ? RfqContactRole.Owner
          : RfqContactRole.Other
    }
    setFormData((prevData) => {
      return {
        ...prevData,
        [changedField]: newValue,
      }
    })
  }

  const validateEmail = useCallback(
    (email: string) => {
      if (isEmail(email)) {
        updateFormDataError('emailAddress', 'success')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          emailAddress: '',
        }))
      } else {
        updateFormDataError('emailAddress', 'error')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          emailAddress: t2(TextId.LABEL_INVALID_EMAIL),
        }))
      }
    },
    [t2],
  )

  const validateCompanyName = useCallback(
    (newValue: string) => {
      if (newValue) {
        updateFormDataError('companyName', 'success')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          companyName: '',
        }))
      } else {
        updateFormDataError('companyName', 'error')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          companyName: t2(TextId.LABEL_INVALID_COMPANY_NAME),
        }))
      }
    },
    [t2],
  )

  const validatePostalCode = useCallback(
    (newValue: string) => {
      var correctFormat =
        newValue.length > 0 && newValue.length < 12 && !newValue.includes('@')
      if (correctFormat) {
        updateFormDataError('postCode', 'success')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          postCode: '',
        }))
      } else {
        updateFormDataError('postCode', 'error')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          postCode: t2(TextId.LABEL_INVALID_ADDRESS_POSTAL_CODE),
        }))
      }
    },
    [t2],
  )

  const validateVatNumber = useCallback(
    (newValue: string) => {
      var isValid = newValue.length < 100 && !newValue.includes('@')
      if (isValid) {
        updateFormDataError('vat', 'success')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          vat: '',
        }))
      } else {
        updateFormDataError('vat', 'error')
        setFormDataErrorMessage((prev) => ({
          ...prev,
          vat: t2(TextId.LABEL_INVALID_VAT_NUMBER),
        }))
      }
    },
    [t2],
  )

  const validateCheckedConsent = useCallback((checked: boolean) => {
    if (checked === true) {
      updateFormDataError('consent', 'success')
    } else {
      updateFormDataError('consent', 'error')
    }
  }, [])

  const validateReseller = useCallback(
    (selectedDealer: Dealer | null, dontKnowDealer: boolean | null) => {
      let newError: 'default' | 'error' | 'success' | undefined = 'error'
      let newErrorMessage = t2(TextId.LABEL_INVALID_RESELLER)
      if (selectedDealer || dontKnowDealer) {
        newError = 'success'
        newErrorMessage = ''
      }
      updateFormDataError('reseller', newError)
      setFormDataErrorMessage((prev) => ({
        ...prev,
        reseller: newErrorMessage,
      }))
    },
    [t2],
  )

  // Get resellers for market
  useEffect(() => {
    if (!marketSettings?.sisEnabled) {
      return
    }
    const marketIso = marketSettings?.marketIso

    if (!marketIso) {
      return
    }
    if (!client) {
      return
    }
    const asyncWrapper = async () => {
      try {
        const dealers = await (await client.getDealers(marketIso)).getDealers
        setDealers(dealers)
      } catch {
        console.log('No dealers found.')
      }
    }
    asyncWrapper()
  }, [marketSettings, client])

  // Populate email field using demo mode email address if provided.
  useEffect(() => {
    if (!startupData) {
      return // Not started yet.
    }
    const settings = startupData.settings
    if (settings.demoModeEnabled) {
      const emailStatus = 'success'
      const changedField = 'emailAddress'
      setFormData((prevData) => {
        return {
          ...prevData,
          [changedField]: settings.demoModeEmailAddress,
        }
      })
      updateFormDataError(changedField, emailStatus)
    }
  }, [startupData])

  // Validate reseller data
  const dealerInputValid = useCallback(() => {
    if (!marketSettings?.sisEnabled) {
      return true
    }
    if (formData.dontKnowDealer) {
      return true
    }
    if (formData.reseller) {
      return true
    }
    return false
  }, [marketSettings, formData])

  // Update and validate input data from redux if any
  useEffect(() => {
    if (!previousRfqInput) {
      return
    }
    validateEmail(previousRfqInput.contactEmail)
    validateCompanyName(previousRfqInput.companyName)
    validatePostalCode(previousRfqInput.companyPostalCode)
  }, [
    previousRfqInput,
    formData,
    validateCompanyName,
    validatePostalCode,
    validateEmail,
  ])

  const submitHandler = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault()
      if (!startupData) {
        return
      }
      const sessionId = sessionInitData?.sessionId
      if (!sessionId) {
        return
      }
      if (!marketLanguage) {
        return
      }
      let mandatoryFieldsEntered = false
      if (
        formDataError.emailAddress === TegelInputStatus.Success &&
        formDataError.companyName === TegelInputStatus.Success &&
        formDataError.postCode === TegelInputStatus.Success &&
        formDataError.consent === TegelInputStatus.Success &&
        dealerInputValid()
      ) {
        mandatoryFieldsEntered = true
        setErrorMessageSend('')
      } else {
        mandatoryFieldsEntered = false
        setErrorMessageSend(t2(TextId.LABEL_INVALID_FORM_INPUT))

        if (formDataError.companyName !== TegelInputStatus.Success) {
          updateFormDataError('companyName', 'error')
          setFormDataErrorMessage((prev) => ({
            ...prev,
            companyName: t2(TextId.LABEL_INVALID_COMPANY_NAME),
          }))
        }
        if (formDataError.postCode !== TegelInputStatus.Success) {
          updateFormDataError('postCode', 'error')
          setFormDataErrorMessage((prev) => ({
            ...prev,
            postCode: t2(TextId.LABEL_INVALID_ADDRESS_POSTAL_CODE),
          }))
        }
        if (formDataError.consent !== TegelInputStatus.Success) {
          updateFormDataError('consent', 'error')
        }
        if (formDataError.emailAddress !== TegelInputStatus.Success) {
          updateFormDataError('emailAddress', 'error')
          setFormDataErrorMessage((prev) => ({
            ...prev,
            emailAddress: t2(TextId.LABEL_INVALID_EMAIL),
          }))
        }
        if (!dealerInputValid()) {
          updateFormDataError('reseller', 'error')
          setFormDataErrorMessage((prev) => ({
            ...prev,
            reseller: 'Please select a dealer.', // TODO: Add a translation text symbol??
          }))
        }
      }
      const mode = startupData.settings.demoModeEnabled
        ? RfqMode.Demo
        : RfqMode.Normal
      const debugEmail = startupData.settings.demoModeEnabled
        ? startupData.settings.demoModeEmailAddress
        : null
      const campaignIdentity = getUrlCidValue()
      if (mandatoryFieldsEntered) {
        const rfqParams: RfqParams = {
          campaignIdentity,
          mode,
          debugEmail,
          dealerId: formData.reseller?.scaniaId,
          companyName: formData.companyName,
          companyPostalCode: formData.postCode,
          companyCity: formData.city,
          contactPhone: formData.telephoneNumber,
          disclaimerAccepted: formData.consent,
          financeInterest: formData.finance,
          newsletter: formData.newsletter,
          contactFirstName: formData.firstName,
          contactFamilyName: formData.surname,
          contactEmail: formData.emailAddress,
          contactRole: formData.role || RfqContactRole.Other,
          messageToDealer: formData.comment,
          organizationNumber: formData.organizationNumber,
          vatNumber: formData.vat,
          language: marketLanguage.language,
          sessionId: sessionId,
        }
        handleRfqSendClick(rfqParams)
        dispatch(setRfqInput(rfqParams))
      }
    },
    [
      formData,
      formDataError,
      dealerInputValid,
      dispatch,
      handleRfqSendClick,
      marketLanguage,
      sessionInitData,
      startupData,
      t2,
    ],
  )

  const updateFormDataError = (
    changedField: string,
    errorState: 'success' | 'error',
  ) => {
    setFormDataError((prevData) => {
      return {
        ...prevData,
        [changedField]: errorState,
      }
    })
  }

  // Build url for image download
  useEffect(() => {
    async function getImageUrl(imageType: string, bgColorArgb: string) {
      if (!client || !sessionId) {
        return
      }
      const flexParams: FlexImageParameters = {
        series: 'EXTERIOR',
        frame: 2,
        bgColor: bgColorArgb,
        autoCrop: ['top', 'bottom', 'left', 'right'],
        margin: [40, 40, 40, 40],
        imageType: imageType,
        size: [1280, 1024],
        useBoundingBoxes: false,
      }
      const requestParams: FlexImageV2RequestParams = {
        sessionId,
        flexParams,
        showBodyBuild: false,
      }
      const response = await client.flexImageV2(requestParams)
      if (response.error === SESSION_FAILURE) {
        return null
      }
      if (response.error) {
        console.error(response.error)
        return null
      }
      if (!response.success?.url) {
        console.error('Failed to find image URL in response.')
        return null
      }
      return response.success.url
    }
    getImageUrl('jpg', '#FFFFFFFF').then((result) => {
      setImageJpgUrl(result || undefined)
    })
  }, [client, sessionId])

  const handleCloseClick = useCallback(() => {
    dispatch(closeAllModals())
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    const relativeUrl =
      summaryOrRfqOpenedFromPage === GuidedViewPageType.allView
        ? buildRelativeAllViewPage(marketLanguage)
        : summaryOrRfqOpenedFromPage === GuidedViewPageType.guidedView
        ? buildRelativeGvPage(marketLanguage)
        : buildRelativeOverviewPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
  }, [dispatch, marketLanguage, navigate, summaryOrRfqOpenedFromPage])

  const handleShowMoreInformationMobile = () => {
    if (mobileShowMinisummary) {
      setMobileShowMinisummary(false)
    } else {
      setMobileShowMinisummary(true)
    }
  }

  // Get colorItems and nonColorItems from miniSummaryState
  useEffect(() => {
    miniSummaryState?.items?.forEach((element) => {
      if (element.itemId.startsWith(FAMILY_ID_APPLICATION)) {
        setApplication(element.itemText)
        return
      }
    })
  }, [marketDenomination, miniSummaryState, truckInfo, t2])

  usePageUpdate(handleFatalError, handleSessionFailure)

  // Adobe user tracking.
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    dispatch(
      pushPageViewTrackingEvent({
        pageName: ScaniaAdobeTrackingPageName.GvRfq,
        marketLanguage,
      }),
    )
  }, [dispatch, marketLanguage])

  return (
    <Overlay ref={rootRef}>
      <LeftSection>
        <SubHeader>
          {isTabletOrSmaller() && <div></div>}
          <TdsButton
            variant="ghost"
            text={t2(TextId.ACTION_LABEL_BACK_CONFIGURATION)}
            onClick={handleCloseClick}
          >
            <TdsIcon slot="icon" size="20px" name="back" />
          </TdsButton>
        </SubHeader>
        <Header>{t2(TextId.ACTION_LABEL_GET_CONSULTATION)}</Header>
        <form onSubmit={submitHandler}>
          <InputSection>
            <StepperText>{t2(TextId.RFQ1_WHAT_HAPPENS_NEXT)}</StepperText>
            <StepperWrapper>
              <StepWrapper>
                <StepIconAndLineWrapper>
                  <StepperIconWrapper $currentStep={true}>
                    <SvgDocument />
                  </StepperIconWrapper>
                  <StepLine />
                </StepIconAndLineWrapper>
                <StepText>
                  {isTabletOrSmaller()
                    ? t2(TextId.RFQ1_INFORMATION_SHORT)
                    : t2(TextId.RFQ1_INFORMATION)}
                </StepText>
              </StepWrapper>
              <StepWrapper>
                <StepIconAndLineWrapper>
                  <StepperIconWrapper $currentStep={false}>
                    <SvgSend />
                  </StepperIconWrapper>
                  <StepLine />
                </StepIconAndLineWrapper>
                <StepText>
                  {isTabletOrSmaller()
                    ? t2(TextId.RFQ1_SEND_SHORT)
                    : t2(TextId.RFQ1_SEND)}
                </StepText>
              </StepWrapper>
              <StepWrapper>
                <StepIconAndLineWrapper>
                  <StepperIconWrapper $currentStep={false}>
                    <SvgEmail />
                  </StepperIconWrapper>
                </StepIconAndLineWrapper>
                <StepText>
                  {isTabletOrSmaller()
                    ? t2(TextId.RFQ1_CONTACT_SHORT)
                    : t2(TextId.RFQ1_CONTACT)}
                </StepText>
              </StepWrapper>
            </StepperWrapper>
          </InputSection>
          <MiniSummaryMobileWrapper>
            <MiniSummaryAccordionWrapper>
              <MiniSummaryMobileImageWrapper>
                {imageJpgUrl ? (
                  <img src={imageJpgUrl} alt="Your truck"></img>
                ) : (
                  <TdsSpinner size="sm" />
                )}
              </MiniSummaryMobileImageWrapper>
              <MiniSummaryMobileHeader>
                {marketDenomination}
              </MiniSummaryMobileHeader>
              <MiniSummaryMobileMoreInformationWrapper
                onClick={handleShowMoreInformationMobile}
              >
                <MiniSummaryMobileMoreInformationText>
                  {t2(TextId.DESCRIPTION_VIEW_INFORMATION)}
                </MiniSummaryMobileMoreInformationText>
                <SvgChevronDown />
              </MiniSummaryMobileMoreInformationWrapper>
            </MiniSummaryAccordionWrapper>
            {mobileShowMinisummary && (
              <MiniummaryMobileContent>
                <MiniSummarySection>
                  <MiniSummary showPackages={false} />
                </MiniSummarySection>
              </MiniummaryMobileContent>
            )}
          </MiniSummaryMobileWrapper>
          <SectionHeader>{t2(TextId.HEADER_QR_FORM_USER_INFO)}</SectionHeader>
          <TextfieldsWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_FIRST_NAME)}</LabelInputField>
              <TdsTextField
                size="md"
                name="firstName"
                value={formData.firstName}
                onInput={handleInputChangeTextfield}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_LAST_NAME)}</LabelInputField>
              <TdsTextField
                size="md"
                name="surname"
                value={formData.surname}
                onInput={handleInputChangeTextfield}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_EMAIL)}</LabelInputField>
              <LabelCompulsory>*</LabelCompulsory>
              <TdsTextField
                size="md"
                name="emailAddress"
                value={formData.emailAddress}
                onInput={handleInputChangeTextfield}
                state={formDataError.emailAddress}
                helper={formDataErrorMessage.emailAddress}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_PHONE_NUMBER)}</LabelInputField>
              <TdsTextField
                size="md"
                name="telephoneNumber"
                placeholder={phoneNumberInstructions}
                value={formData.telephoneNumber}
                onInput={handleInputChangeTextfield}
              />
            </TextFieldWrapper>
          </TextfieldsWrapper>
          <SectionHeader>
            {t2(TextId.HEADER_QR_FORM_COMPANY_INFO)}
          </SectionHeader>
          <TextfieldsWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_COMPANY_NAME)}</LabelInputField>
              <LabelCompulsory>*</LabelCompulsory>
              <TdsTextField
                size="md"
                name="companyName"
                value={formData.companyName}
                onInput={handleInputChangeTextfield}
                state={formDataError.companyName}
                helper={formDataErrorMessage.companyName}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>
                {t2(TextId.LABEL_ADDRESS_POSTAL_CODE)}
              </LabelInputField>
              <LabelCompulsory>*</LabelCompulsory>
              <TdsTextField
                size="md"
                name="postCode"
                value={formData.postCode}
                onInput={handleInputChangeTextfield}
                state={formDataError.postCode}
                helper={formDataErrorMessage.postCode}
                placeholder={t2(TextId.LABEL_ADDRESS_POSTAL_CODE)}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_ADDRESS_CITY)}</LabelInputField>
              <TdsTextField
                size="md"
                name="city"
                value={formData.city}
                onInput={handleInputChangeTextfield}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>
                {t2(TextId.LABEL_ORGANIZATION_NUMBER)}
              </LabelInputField>
              <TdsTextField
                size="md"
                name="organizationNumber"
                value={formData.organizationNumber}
                onInput={handleInputChangeTextfield}
              />
            </TextFieldWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.LABEL_VAT_NUMBER)}</LabelInputField>
              <TdsTextField
                size="md"
                name="vat"
                value={formData.vat}
                onInput={handleInputChangeTextfield}
                state={formDataError.vat}
                helper={formDataErrorMessage.vat}
              />
            </TextFieldWrapper>
          </TextfieldsWrapper>
          <SectionHeader>{t2(TextId.RFQ1_CUSTOMER_ROLE_OTHER)}</SectionHeader>
          <TextfieldsWrapper>
            <TextFieldWrapper>
              <LabelInputField>{t2(TextId.RFQ1_ROLE)}</LabelInputField>
              <TdsDropdown
                size="lg"
                open-direction="auto"
                name="role"
                defaultValue={formData.role || undefined}
                onTdsChange={handleInputChangeDropdown}
              >
                {[
                  { text: t2(TextId.RFQ1_CUSTOMER_ROLE_OWNER), id: 'Owner' },
                  {
                    text: t2(TextId.RFQ1_CUSTOMER_ROLE_DRIVER),
                    id: 'Driver',
                  },
                  { text: t2(TextId.RFQ1_CUSTOMER_ROLE_OTHER), id: 'Other' },
                ].map((role) => (
                  <TdsDropdownOption key={role.id} value={role.id}>
                    {role.text}
                  </TdsDropdownOption>
                ))}
              </TdsDropdown>
            </TextFieldWrapper>
            {marketSettings?.sisEnabled === true && (
              <TextFieldWrapper
                key={
                  (formData.reseller?.scaniaId || '') + formData.dontKnowDealer
                }
              >
                <LabelInputField>{t2(TextId.RFQ1_RESELLER)}</LabelInputField>
                <LabelCompulsory>*</LabelCompulsory>
                <TdsDropdown
                  name="reseller"
                  filter={true}
                  defaultValue={formData.reseller?.scaniaId || undefined}
                  error={(formDataError.reseller === 'error').valueOf()}
                  helper={formDataErrorMessage.reseller}
                  onTdsChange={handleInputChangeDropdown}
                  key={formDataErrorMessage.reseller}
                >
                  {dealers &&
                    dealers.map((dealer) => (
                      <TdsDropdownOption
                        key={dealer.scaniaId}
                        value={dealer.scaniaId || undefined}
                      >
                        {dealer.organizationName}
                      </TdsDropdownOption>
                    ))}
                </TdsDropdown>
                {shouldShowDontKnowDealerCheckbox && (
                  <DontKnowDealerWrapper>
                    <TdsCheckbox
                      name="dontKnowDealer"
                      value="dontKnowDealer"
                      checked={formData.dontKnowDealer}
                      onTdsChange={handleInputChangeCheckbox}
                    >
                      <span slot="label">
                        {t2(TextId.RFQ1_I_DONT_KNOW_DEALER)}
                      </span>
                    </TdsCheckbox>
                  </DontKnowDealerWrapper>
                )}
              </TextFieldWrapper>
            )}
          </TextfieldsWrapper>
          {!(
            marketSettings?.sisEnabled === false ||
            (marketSettings?.sisEnabled === true && !formData.reseller)
          ) && (
            <DealerTextBox>
              <DealerTextBoxHeader>
                {formData.reseller?.organizationName}
              </DealerTextBoxHeader>
              <DealerTextBoxAddress>
                {formData.reseller?.visitingAddress?.streetName +
                  ' ' +
                  (formData.reseller?.visitingAddress?.streetNumber || '') +
                  (formData.reseller?.visitingAddress?.streetLetter || '')}
              </DealerTextBoxAddress>
              <DealerTextBoxAddress>
                {formData.reseller?.visitingAddress?.postalCode}
              </DealerTextBoxAddress>
              <DealerTextBoxAddress>
                {formData.reseller?.visitingAddress?.city}
              </DealerTextBoxAddress>
              <DealerTextBoxPhone>
                {formData.reseller?.customerReceptionPhoneNumber}
              </DealerTextBoxPhone>
            </DealerTextBox>
          )}
          <TextAreaWrapper>
            <TdsTextarea
              label={t2(TextId.LABEL_COMMENTS)}
              labelPosition="outside"
              placeholder={t2(TextId.RFQ1_COMMENT_PLACEHOLDER)}
              name="comment"
              value={formData.comment}
              onInput={handleInputChangeTextfield}
            />
          </TextAreaWrapper>
          <MarketingConsentWrapper>
            <h5 style={{ marginBottom: '16px' }}>
              {t2(TextId.RFQ1_MARKETING_CONSENT)}
            </h5>
            <MarketingConsentText>
              {t2(TextId.RFQ1_PERSONEL_DATA_DESCRIPTION1)}
            </MarketingConsentText>
            <MarketingConsentText>
              {t2(TextId.RFQ1_PERSONEL_DATA_DESCRIPTION2)}
            </MarketingConsentText>
            <CheckboxWrapper>
              <TdsCheckbox
                name="consent"
                value="consent"
                checked={formData.consent}
                onTdsChange={handleInputChangeCheckbox}
              >
                <div
                  slot="label"
                  style={{
                    fontWeight: 'bold',
                    color: formDataError.consent === 'error' ? 'red' : '',
                  }}
                >
                  <LabelCompulsory>*</LabelCompulsory>{' '}
                  {t2(TextId.RFQ1_PERSONEL_DATA)}{' '}
                </div>
              </TdsCheckbox>
              <TdsCheckbox
                name="finance"
                value="finance"
                checked={formData.finance}
                onTdsChange={handleInputChangeCheckbox}
              >
                <div slot="label" style={{ fontWeight: 'bold' }}>
                  {t2(TextId.RFQ1_FINANCE_DESCRIPTION)}
                </div>
              </TdsCheckbox>
              <TdsCheckbox
                name="newsletter"
                value="newsletter"
                checked={formData.newsletter}
                onTdsChange={handleInputChangeCheckbox}
              >
                <div slot="label" style={{ fontWeight: 'bold' }}>
                  {t2(TextId.RFQ1_NEWSLETTER)}{' '}
                  <CheckboxSmallText>
                    {t2(TextId.RFQ1_NEWSLETTER_DESCRIPTION)}
                  </CheckboxSmallText>
                </div>
              </TdsCheckbox>
            </CheckboxWrapper>
          </MarketingConsentWrapper>
          {startupData?.settings.demoModeEnabled && (
            <DemoModeMessage>
              <h3>Demo Mode is active.</h3>
              <p>
                Using demo mode address:{' '}
                {startupData.settings.demoModeEmailAddress}
              </p>
            </DemoModeMessage>
          )}
          {errorMessageSend && (
            <TdsMessage
              variant="error"
              header={errorMessageSend}
              style={{ marginTop: '16px' }}
            />
          )}
          <ActionsWrapper>
            <TdsButton
              variant="primary"
              text={t2(TextId.LABEL_ACTION_REQUEST_OFFER)}
              type="submit"
            />
            <TdsButton
              variant="secondary"
              text={t2(TextId.LABEL_ACTION_CANCEL)}
              onClick={handleCloseClick}
            />
          </ActionsWrapper>
        </form>
      </LeftSection>
      <RightSection>
        <TruckSection>
          {imageJpgUrl ? (
            <img src={imageJpgUrl} alt="Your truck"></img>
          ) : (
            <TdsSpinner size="md" />
          )}
        </TruckSection>
        <MiniSummarySection>
          <HeaderWrapperMiniSummary>
            <HeaderMiniSummary>
              {truckInfo?.savedAsName ||
                truckInfo?.factoryModelName ||
                marketDenomination}
            </HeaderMiniSummary>
            <ApplicationWrapper>{application}</ApplicationWrapper>
          </HeaderWrapperMiniSummary>
          <MiniSummary showPackages={false} />
        </MiniSummarySection>
      </RightSection>
    </Overlay>
  )
}
